// FILE: src/services/roles.js
// Staff roles and permissions (RBAC). Failures reject with the server body
// (fetchWithAuth), so pages show them with errorMessage().
//   GET    /api/admin/permissions        → { groups: [{ id, label, permissions: [{ key, label, description }] }], permissions }
//   GET    /api/admin/roles              → { roles: [role] }
//   GET    /api/admin/roles/:id          → role + memberList
//   POST   /api/admin/roles              { name, description, permissions } → role
//   PATCH  /api/admin/roles/:id          { name?, description?, permissions? } → role
//   DELETE /api/admin/roles/:id          → { message, id }
//   POST   /api/admin/staff              { email, firstName, lastName, staffRoleId, staffRegistration? } → { message, user }
//   PATCH  /api/admin/users/:id/role     { staffRoleId } → user
// role = { id, name, description, permissions, system, key, members, editable, renamable, deletable }
import { useCallback, useEffect, useState } from "react";
import fetchWithAuth from "../utils/fetchWithAuth";

const enc = encodeURIComponent;
const json = (method, body) => ({ method, body: JSON.stringify(body) });

export const getPermissionCatalogue = () => fetchWithAuth("/api/admin/permissions");
export const listRoles = () => fetchWithAuth("/api/admin/roles");
export const getRole = (id) => fetchWithAuth(`/api/admin/roles/${enc(id)}`);
export const createRole = (body) => fetchWithAuth("/api/admin/roles", json("POST", body));
export const updateRole = (id, body) => fetchWithAuth(`/api/admin/roles/${enc(id)}`, json("PATCH", body));
export const deleteRole = (id) => fetchWithAuth(`/api/admin/roles/${enc(id)}`, { method: "DELETE" });
export const inviteStaff = (body) => fetchWithAuth("/api/admin/staff", json("POST", body));
export const setStaffRole = (userId, staffRoleId) => fetchWithAuth(`/api/admin/users/${enc(userId)}/role`, json("PATCH", { staffRoleId }));
export const listStaff = ({ page = 1, limit = 100, search } = {}) =>
  fetchWithAuth(`/api/admin/users?role=admin&page=${page}&limit=${limit}${search ? `&search=${enc(search)}` : ""}`);

// The DDI registration number is free text up to this length (server rule).
export const STAFF_REGISTRATION_MAX = 40;

/**
 * { roles, error, loading, reload } — the roles for a picker or the roles
 * page. With `enabled: false` nothing is fetched (e.g. editing a student).
 */
export function useRoles({ enabled = true } = {}) {
  const [state, setState] = useState({ roles: null, error: null, loading: enabled });
  const reload = useCallback(() => {
    setState((s) => ({ ...s, loading: true, error: null }));
    return listRoles()
      .then((data) => setState({ roles: Array.isArray(data?.roles) ? data.roles : [], error: null, loading: false }))
      .catch((error) => setState({ roles: null, error: error || "INTERNAL_ERROR", loading: false }));
  }, []);
  useEffect(() => {
    if (enabled) reload();
  }, [reload, enabled]);
  return { ...state, reload };
}
